import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export interface EpcMilestone {
  id: number;
  projectId: number;
  name: string;
  description: string | null;
  sequence: number;
  weight: number;
  status: 'pending' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  progressPct: number;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  remarks: string | null;
  createdAt: string;
}

export interface EpcProjectProgress {
  projectId: number;
  milestoneCount: number;
  overallProgressPct: number;
  completed: number;
  inProgress: number;
  pending: number;
  byStatus: Record<string, number>;
}

export interface EpcHandoverDoc { id: number; docType: string; fileName: string; fileUrl: string; createdAt: string }
export interface EpcHandover {
  id: number;
  handoverCode: string;
  projectId: number;
  status: 'draft' | 'submitted' | 'accepted' | 'rejected';
  clientRepresentative: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  remarks: string | null;
  createdAt: string;
  documents?: EpcHandoverDoc[];
}

interface Paginated<T> { data: T[]; meta: PaginationMeta }

export const epcApi = {
  // Milestones
  async listMilestones(params: Record<string, unknown> = {}): Promise<Paginated<EpcMilestone>> {
    const { data } = await apiClient.get<ApiSuccess<EpcMilestone[]>>('/epc-milestones', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  createMilestone: async (body: { projectId: number; name: string; description?: string; sequence?: number; weight?: number; plannedStartDate?: string; plannedEndDate?: string }) =>
    (await apiClient.post<ApiSuccess<EpcMilestone>>('/epc-milestones', body)).data.data,
  setProgress: async (id: number, body: { status?: string; progressPct?: number; remarks?: string }) =>
    (await apiClient.post<ApiSuccess<EpcMilestone>>(`/epc-milestones/${id}/progress`, body)).data.data,
  removeMilestone: async (id: number) => (await apiClient.delete(`/epc-milestones/${id}`)).data,
  projectProgress: async (projectId: number) =>
    (await apiClient.get<ApiSuccess<EpcProjectProgress>>(`/epc-milestones/project/${projectId}/progress`)).data.data,

  // Handovers
  async listHandovers(params: Record<string, unknown> = {}): Promise<Paginated<EpcHandover>> {
    const { data } = await apiClient.get<ApiSuccess<EpcHandover[]>>('/epc-handovers', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  getHandover: async (id: number) => (await apiClient.get<ApiSuccess<EpcHandover>>(`/epc-handovers/${id}`)).data.data,
  createHandover: async (body: { projectId: number; clientRepresentative?: string; remarks?: string }) =>
    (await apiClient.post<ApiSuccess<EpcHandover>>('/epc-handovers', body)).data.data,
  submitHandover: async (id: number) => (await apiClient.post<ApiSuccess<EpcHandover>>(`/epc-handovers/${id}/submit`, {})).data.data,
  acceptHandover: async (id: number, remarks?: string) => (await apiClient.post<ApiSuccess<EpcHandover>>(`/epc-handovers/${id}/accept`, { remarks })).data.data,
  rejectHandover: async (id: number, reason: string) => (await apiClient.post<ApiSuccess<EpcHandover>>(`/epc-handovers/${id}/reject`, { reason })).data.data,
  addDocument: async (id: number, body: { docType: string; fileName: string; fileUrl: string }) =>
    (await apiClient.post<ApiSuccess<EpcHandover>>(`/epc-handovers/${id}/documents`, body)).data.data,
};
